import React, { useState, useEffect, useCallback } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import { useGlobalAPI } from "../../../hooks/useGlobalAPI";
import { useGraphData } from "../../../stores/GraphDataContext";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import EdgeDurationHistogramSettings from "./EdgeDurationHistogramSettings";
import { getFileMetadata } from "../LeadTimeHistogram/api/leadTimeHistogramApi";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const GRAY_BG = "rgba(156, 163, 175, 0.6)";
const GRAY_BORDER = "rgba(156, 163, 175, 1)";
const BLUE_BG = "rgba(54, 162, 235, 0.6)";
const BLUE_BORDER = "rgba(54, 162, 235, 1)";
const ORANGE_BG = "rgba(255, 159, 64, 0.6)";
const ORANGE_BORDER = "rgba(255, 159, 64, 1)";
const PURPLE_BG = "rgba(153, 102, 255, 0.6)";
const PURPLE_BORDER = "rgba(153, 102, 255, 1)";

const DEFAULT_EDGE_CONFIG = {
  // Use dynamic selection (picks first valid edge from graph)
  edgeId: null, // null = auto-select first edge
};

const EdgeDurationHistogram = ({
  activeFile,
  onRefresh,
  onGetHeaderActions,
}) => {
  const api = useGlobalAPI(activeFile);
  const { filteredGraphData: graphData } = useGraphData();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Edge selection state
  const [objectType, setObjectType] = useState(
    DEFAULT_EDGE_CONFIG.objectType || ""
  );
  const [sourceActivity, setSourceActivity] = useState(
    DEFAULT_EDGE_CONFIG.sourceActivity || ""
  );
  const [targetActivity, setTargetActivity] = useState(
    DEFAULT_EDGE_CONFIG.targetActivity || ""
  );

  const [fileMetadata, setFileMetadata] = useState(null);
  const [availableObjectTypes, setAvailableObjectTypes] = useState([]);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  // Graph loading state
  const [isGraphLoaded, setIsGraphLoaded] = useState(false);

  // Track if edge was selected from graph click
  const [edgeSelectedFromGraph, setEdgeSelectedFromGraph] = useState(false);

  // Reset everything when file changes
  useEffect(() => {
    setData(null);
    setLoading(false);
    setError(null);
    setHasFetched(false);
    setFileMetadata(null);
    setAvailableObjectTypes([]);
    setObjectType(DEFAULT_EDGE_CONFIG.objectType || "");
    setSourceActivity(DEFAULT_EDGE_CONFIG.sourceActivity || "");
    setTargetActivity(DEFAULT_EDGE_CONFIG.targetActivity || "");
    setIsGraphLoaded(false);
  }, [activeFile]);

  // Listen for edge clicks from process graph
  useEffect(() => {
    const handleEdgeClickFromGraph = (event) => {
      const { sourceActivity, targetActivity, objectType, edgeLabel } =
        event.detail || {};

      if (!sourceActivity || !targetActivity) {
        console.warn(
          "⚠️ EdgeDurationHistogram: Invalid edge click event",
          event.detail
        );
        return;
      }

      console.log("🎯 EdgeDurationHistogram: Edge selected from graph", {
        sourceActivity,
        targetActivity,
        objectType,
        edgeLabel,
      });

      // Update edge selection state (modal remains closed - user opens manually)
      setObjectType(objectType || "");
      setSourceActivity(sourceActivity);
      setTargetActivity(targetActivity);
      setEdgeSelectedFromGraph(true);
    };

    window.addEventListener(
      "edge-duration-filter:create",
      handleEdgeClickFromGraph
    );
    return () =>
      window.removeEventListener(
        "edge-duration-filter:create",
        handleEdgeClickFromGraph
      );
  }, []);

  // Wait for graph data to load and auto-select first edge if needed
  useEffect(() => {
    if (!graphData?.edges_performance?.event_couples) {
      setIsGraphLoaded(false);
      return;
    }

    setIsGraphLoaded(true);
    console.log("✅ EdgeDurationHistogram: Graph data loaded", {
      hasGraphData: !!graphData,
      hasEdgesPerformance: !!graphData?.edges_performance,
      objectTypes: Object.keys(
        graphData?.edges_performance?.event_couples || {}
      ),
    });

    // Auto-select first edge if using dynamic selection
    if (
      DEFAULT_EDGE_CONFIG.edgeId === null &&
      !DEFAULT_EDGE_CONFIG.sourceActivity
    ) {
      selectFirstValidEdge();
    }
  }, [graphData]);

  // Function to select first valid edge from graph data
  const selectFirstValidEdge = useCallback(() => {
    if (!graphData?.edges_performance?.event_couples) {
      console.warn("No graph data available for edge selection");
      return;
    }

    const eventCouples = graphData.edges_performance.event_couples;
    const objectTypes = Object.keys(eventCouples);

    if (objectTypes.length === 0) {
      console.warn("No object types found in graph data");
      return;
    }

    // Pick first object type
    const firstObjectType = objectTypes[0];
    const edges = eventCouples[firstObjectType];

    if (!edges || Object.keys(edges).length === 0) {
      console.warn(`No edges found for object type: ${firstObjectType}`);
      return;
    }

    // Pick first edge (format: "sourceActivity,targetActivity")
    const firstEdgeKey = Object.keys(edges)[0];
    const [source, target] = firstEdgeKey.split(",");

    console.log("🎯 Auto-selected first edge:", {
      objectType: firstObjectType,
      sourceActivity: source,
      targetActivity: target,
      edgeKey: firstEdgeKey,
    });

    setObjectType(firstObjectType);
    setSourceActivity(source);
    setTargetActivity(target);
  }, [graphData]);

  // Load file metadata
  useEffect(() => {
    const fetchMetadata = async () => {
      if (!activeFile) {
        setAvailableObjectTypes([]);
        return;
      }

      try {
        const metadata = await getFileMetadata(activeFile);
        setFileMetadata(metadata);

        if (metadata?.object_types) {
          const types = Object.keys(metadata.object_types).sort();
          setAvailableObjectTypes(types);
        }
      } catch (error) {
        console.error("Error fetching metadata:", error);
      }
    };

    fetchMetadata();
  }, [activeFile]);

  const processHistogramData = (responseData) => {
    if (!responseData || !Array.isArray(responseData.bins)) {
      console.warn("Invalid histogram bins data:", responseData);
      return null;
    }

    if (responseData.bins.length === 0) {
      console.warn("Empty bins array");
      return null;
    }

    // Extract labels and values from bins
    const labels = responseData.bins.map((bin) => bin.rangeLabel);
    const values = responseData.bins.map((bin) => bin.count);

    console.log("Processed histogram data:", {
      labels,
      values,
      totalCount: responseData.totalCount,
    });

    // Adjust bar width for single bar case to prevent extremely wide bars
    const binCount = responseData.bins.length;
    const barPercentage = binCount === 1 ? 0.3 : 0.95;
    const categoryPercentage = binCount === 1 ? 0.5 : 0.9;

    return {
      labels,
      datasets: [
        {
          label: "Number of Objects",
          data: values,
          backgroundColor: BLUE_BG,
          borderColor: BLUE_BORDER,
          borderWidth: 1,
          barPercentage: barPercentage,
          categoryPercentage: categoryPercentage,
        },
      ],
    };
  };

  const fetchHistogramData = useCallback(async () => {
    if (!activeFile) {
      setError("No active file selected");
      return;
    }

    if (!isGraphLoaded) {
      setError("Waiting for graph data to load...");
      return;
    }

    if (!objectType || !sourceActivity || !targetActivity) {
      setError(
        "Please select an edge (source activity, target activity, and object type)"
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const componentParams = {
        source_activity: sourceActivity,
        target_activity: targetActivity,
        object_type: objectType,
      };

      console.log("📤 Sending edge histogram request:", componentParams);
      const response = await api.post(
        "/edge-duration-histogram",
        componentParams,
        {},
        "Edge Duration Histogram"
      );
      console.log("📥 Edge histogram response received:", response);

      if (response && response.bins) {
        setData(response);
      } else {
        setData(null);
        setError("No data available for the selected edge");
      }
      setHasFetched(true);
    } catch (err) {
      console.error("❌ Error during data fetch:", err);
      setError(err.message || "Failed to load edge duration data");
      setHasFetched(true);
    } finally {
      setLoading(false);
    }
  }, [
    activeFile,
    objectType,
    sourceActivity,
    targetActivity,
    api,
    isGraphLoaded,
  ]);

  const handleOpenSettings = useCallback(() => {
    setIsSettingsModalOpen(true);
  }, []);


  const handleApplySettings = useCallback(() => {
    setIsSettingsModalOpen(false);
    setEdgeSelectedFromGraph(false); // Clear flag when user loads chart
    fetchHistogramData();
  }, [fetchHistogramData]);

  // Provide header actions to parent (GridComponent)
  const headerActions = React.useMemo(
    () => (
      <button
        onClick={handleOpenSettings}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Open settings"
      >
        <svg
          className="w-3 h-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span className="text-xs">Settings</span>
      </button>
    ),
    [handleOpenSettings]
  );

  useEffect(() => {
    if (onRefresh) {
      onRefresh(fetchHistogramData);
    }
  }, [onRefresh, fetchHistogramData]);

  useEffect(() => {
    if (onGetHeaderActions) {
      onGetHeaderActions(headerActions);
    }
  }, [onGetHeaderActions, headerActions]);

  const chartData = data ? processHistogramData(data) : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: data?.edgeLabel || "Edge Duration Distribution",
        font: {
          size: 16,
          weight: "bold",
        },
      },
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: function () {
            return "";
          },
          label: function (context) {
            const count = context.parsed.y;
            const percentage = data
              ? ((count / data.totalCount) * 100).toFixed(1)
              : 0;
            return `${count} objects (${percentage}%)`;
          },
          afterLabel: function (context) {
            if (data && data.bins && data.bins[context.dataIndex]) {
              const bin = data.bins[context.dataIndex];
              return [`Total: ${data.totalCount} objects`];
            }
            return "";
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: data?.timeUnit ? `Duration (${data.timeUnit})` : "Duration",
          font: {
            size: 13,
            weight: "bold",
          },
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          autoSkip: false, // Show all bin labels
        },
      },
      y: {
        title: {
          display: true,
          text: "Number of Objects",
          font: {
            size: 13,
            weight: "bold",
          },
        },
        beginAtZero: true,
        grace: "15%", // Add 15% headroom above tallest bar
        ticks: {
          precision: 0, // Whole numbers only
          maxTicksLimit: 8, // Limit Y-axis labels for cleaner look
        },
      },
    },
  };

  return (
    <div
      className="h-full flex flex-col p-6"
      style={{ background: "transparent" }}
    >
      {!isGraphLoaded ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">
              Waiting for process graph to load...
            </p>
            <p className="text-gray-500 text-sm mt-2">
              The edge duration histogram requires graph data
            </p>
          </div>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading histogram data...</p>
          </div>
        </div>
      ) : !hasFetched ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Ready to Generate Histogram
            </h3>
            <p className="text-gray-500 mb-4">
              Select an edge from the process graph, or configure an edge
              selection in Settings, then click “Load Chart” to begin.
            </p>
          </div>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <p className="text-red-600 font-medium mb-2">Error Loading Data</p>
            <p className="text-gray-600 text-sm mb-4">{error}</p>
            <button
              onClick={fetchHistogramData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      ) : chartData ? (
        <div className="flex-1 relative min-h-0">
          <Bar data={chartData} options={chartOptions} />
        </div>
      ) : (
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">
            No data available for this configuration. Try adjusting the settings
            or filters.
          </p>
        </div>
      )}

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
        onApply={handleApplySettings}
        title="Edge Duration Histogram Settings"
        isApplyDisabled={
          !activeFile ||
          loading ||
          !isGraphLoaded ||
          !objectType ||
          !sourceActivity ||
          !targetActivity
        }
        applyButtonText={
          loading ? "Loading..." : hasFetched ? "Update Chart" : "Load Chart"
        }
      >
        <EdgeDurationHistogramSettings
          graphData={graphData}
          objectType={objectType}
          onObjectTypeChange={(value) => {
            setObjectType(value);
            setEdgeSelectedFromGraph(false);
          }}
          sourceActivity={sourceActivity}
          onSourceActivityChange={(value) => {
            setSourceActivity(value);
            setEdgeSelectedFromGraph(false);
          }}
          targetActivity={targetActivity}
          onTargetActivityChange={(value) => {
            setTargetActivity(value);
            setEdgeSelectedFromGraph(false);
          }}
          availableObjectTypes={availableObjectTypes}
          loading={loading}
          edgeSelectedFromGraph={edgeSelectedFromGraph}
        />
      </TileSettingsModal>
    </div>
  );
};

export default EdgeDurationHistogram;
